import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { loginWithK12AuthorizationCode } from "../../../requests/ManagementRequests";
import { NotificationManager } from "../../../notifications/NotificationManager";
import i18n from "../../../language/i18n";
import { useApp } from "../../../context/AppContextProvider";
import {
  forgetDeactivatedProfile,
  isLearnerProfileDeactivatedError,
} from "../../../utilization/DeactivatedProfileUtilization";
import connection_production from "../../../config/connection-production.json";
import connection_local from "../../../config/connection-local.json";
import connection_development from "../../../config/connection-development.json";
import { BaseLoadingWithText } from "../../../library";
import MobileAppHandoffCard from "../../../components/common/MobileAppHandoffCard";
import { loginWithK12ScreenStyles } from "./LoginWithK12ScreenStyles";
import {
  isServerUnavailableError,
  resolveBackendMessageKey,
} from "../../../utilization/BackendMessageUtilization";
import { useHandoffLogin } from "../../../hooks/useHandoffLogin";
import {
  buildK12AuthorizeUri,
  isK12LoginConfigured,
} from "../../../utilization/K12LoginUtilization";
import {
  buildMobileAppUrl,
  detectPhonePlatform,
  isAndroidDevice,
  isMobileAppK12State,
  MOBILE_APP_K12_CALLBACK_PATH,
  MOBILE_APP_K12_ENTRY_PATH,
  PhonePlatform,
  storeUrlFor,
} from "../../../utilization/MobileAppHandoffUtilization";

const connection =
  process.env.REACT_APP_NODE_ENV === "local"
    ? connection_local
    : process.env.REACT_APP_NODE_ENV === "development"
    ? connection_development
    : process.env.REACT_APP_NODE_ENV === "production"
    ? connection_production
    : connection_local;

/** sessionStorage key holding the pending OAuth `state` for this browser tab. */
const K12_STATE_STORAGE_KEY = "k12-oauth-state";

/**
 * Does this fragment actually carry a hand-off token pair?
 *
 * NOT `hash.length > 1`. A provider callback can legitimately arrive as `?code=…&state=…#_=_` (an empty
 * fragment is a known OAuth-era artefact), and routing that to the hand-off leg would `replaceState` the
 * URL immediately — destroying a single-use `code` beyond recovery — and then report a credentials error
 * for a perfectly good SSO callback.
 */
const carriesHandoffTokens = (currentHash) =>
  new URLSearchParams(String(currentHash ?? "").replace(/^#/, "")).get(
    "accessToken"
  ) !== null;

/** What the screen shows instead of its spinner while it hands the learner over to the mobile app. */
type MobileAppHandoff = {
  /** `entry`: a phone opened the K12 tile; `callback`: K12Net returned to a sign-in the app started. */
  kind: "entry" | "callback";
  platform: PhonePlatform;
  appUrl: string;
};

/**
 * K12Net SSO landing screen — the learner's entry.
 *
 * **One route, three legs**, told apart by what the URL carries:
 *  1. nothing → start the flow (redirect to K12Net's authorize endpoint),
 *  2. `?code=&state=` → K12Net's callback; hand the code to our backend,
 *  3. `#accessToken=…&refreshToken=…` → our own hand-off coming back; complete the login.
 *
 * **The exchange moved SERVER-SIDE (Aug 2026), and that is the point of this screen's rewrite.** It used to
 * do two hops: ask the backend for the K12 user id, then post that id to
 * `/users/authenticate/with-profile/by-external-resource` to get a session. That second endpoint is
 * permit-listed and takes the identity from its request body with NO proof of possession — so anyone who
 * learned a pupil's K12 GUID could mint that pupil's session, and the only thing standing in the way was
 * that the id happens to be a GUID. It was also why K12 SSO could never be offered to teachers: the same
 * endpoint, widened to staff, would have been an unauthenticated path into the management console.
 *
 * Now the authorization code — single-use, short-lived, and issued by K12Net to the browser that actually
 * completed the login — is the credential, and the backend resolves the identity from K12Net's own
 * `userinfo` response. The id is never handed to this page and never accepted back from it.
 *
 * **Phones go to the mobile app (Oct 2026).** K12Net's mobile app can only open this route in the phone's browser,
 * where the web app is not supported. So on a phone, leg 1 offers the installed app (or the store) instead of
 * starting a web sign-in; with the app installed, Universal Links / App Links normally open it before this page even
 * loads. The app then runs its own K12 sign-in against this same registered callback, marking its `state` with
 * `MOBILE_APP_K12_STATE_PREFIX`. Nothing about the code exchange changes: the app posts to the same backend endpoint.
 *  - iOS never reaches this page on the way back: its ASWebAuthenticationSession claims this https URL as its
 *    callback (domain-verified via the AASA `webcredentials` entry) and ends before the page loads.
 *  - Android comes back here, and leg 2 forwards the code through an `intent://` URL pinned to our package.
 *
 * The forward is Android-ONLY on purpose. An `m.` state is attacker-choosable — anyone can craft an authorize link
 * carrying one — so this page must never hand a code to a channel another app could claim. A package-pinned intent
 * cannot be claimed; an iOS custom scheme can (iOS does not reserve schemes, RFC 8252 §8.1), and PKCE would not help
 * because in that attack the attacker also picks the `code_challenge`. Anywhere but Android an `m.` state therefore
 * falls through to the ordinary web check and is refused as an expired link.
 */
const LoginWithK12Screen = () => {
  const navigate = useNavigate();
  const { hash, search } = useLocation();
  const { isFullScreen, setIsFullScreen, setBoarding } = useApp();
  const { completeHandoffLogin, completeHandoffOrNavigate } =
    useHandoffLogin();
  const K12_REDIRECT_URI = `${connection.app.url}/login/with-k12`;
  const [mobileAppHandoff, setMobileAppHandoff] =
    useState<MobileAppHandoff | null>(null);

  useEffect(() => {
    if (isFullScreen === false) {
      setIsFullScreen(true);
    }
  }, []);

  useEffect(() => {
    /**
     * Bail out to the password screen WITH a message.
     *
     * Every failure below routes through here. These were once bare `return`s after a `console.log`, so the
     * screen sat on its spinner forever — a K12 consent denial, an expired code, and a student with no
     * Catchup profile all looked identical to "still loading", with no way back but the browser's back
     * button.
     */
    const failToLogin = (messageKey: string) => {
      NotificationManager.error(i18n.t(messageKey));
      navigate("/login");
      // App.tsx exempts this route from its phone "device not supported" modal, but it sits outside the router and
      // only re-checks on resize — so without this a phone would reach /login with the modal still off.
      window.dispatchEvent(new Event("resize"));
    };

    /**
     * Strip `?code=`/`?state=` from the URL and history as soon as they are read. An authorization code is
     * single-use and short-lived, but it has no business sitting in history, a synced browser profile, or
     * an outbound Referer.
     */
    const clearQueryFromHistory = () => {
      window.history.replaceState({}, document.title, window.location.pathname);
    };

    const searchParams = new URLSearchParams(search);
    const userAgent = window.navigator.userAgent;

    /**
     * Leg 1 on a phone: offer the app instead of a web sign-in the phone cannot use afterwards.
     *
     * Android gets an `intent://` URL whose fallback is the Play Store, so one navigation covers both "installed" and
     * "not installed"; it is also fired straight away, and the buttons stay for when Chrome wants a tap first. iOS has
     * no such fallback — a custom scheme with no app behind it shows Safari's "invalid address" alert — so there it
     * waits for the learner to choose.
     */
    const offerMobileApp = (platform: PhonePlatform) => {
      const appUrl = buildMobileAppUrl(
        platform,
        MOBILE_APP_K12_ENTRY_PATH,
        undefined,
        platform === "android" ? storeUrlFor(platform) : undefined
      );
      setMobileAppHandoff({ kind: "entry", platform, appUrl });
      if (platform === "android") {
        window.location.href = appUrl;
      }
    };

    /**
     * Leg 2 for a sign-in the Android app started: pass K12Net's answer (code or error) back to the app untouched.
     *
     * The `state` is NOT checked here — this tab never had it; the app did, and the app compares it before it
     * exchanges anything. Callers must only use this on Android (see the component doc): the URL is pinned to our
     * package, which is what makes forwarding an attacker-chosen `state` harmless.
     */
    const returnToAndroidApp = (state: string) => {
      const platform: PhonePlatform = "android";
      const appUrl = buildMobileAppUrl(platform, MOBILE_APP_K12_CALLBACK_PATH, {
        code: searchParams.get("code"),
        error: searchParams.get("error"),
        state,
      });
      clearQueryFromHistory();
      setMobileAppHandoff({ kind: "callback", platform, appUrl });
      window.location.href = appUrl;
    };

    const startK12Login = () => {
      // Refuse to redirect when the integration is not configured for this build: without a client id /
      // base URL the authorize URL is literally "undefined/GWCore.Web/..." and the student lands on a
      // broken third-party page.
      if (!isK12LoginConfigured()) {
        failToLogin("k12_login_not_configured");
        return;
      }
      const oauthState = window.crypto.randomUUID();
      window.sessionStorage.setItem(K12_STATE_STORAGE_KEY, oauthState);
      window.location.href = buildK12AuthorizeUri(K12_REDIRECT_URI, oauthState);
    };


    const completeK12Callback = async () => {
      const returnedState = searchParams.get("state");
      if (
        isMobileAppK12State(returnedState) &&
        isAndroidDevice(userAgent)
      ) {
        returnToAndroidApp(returnedState as string);
        return;
      }

      // K12 reports consent denial / errors as ?error=..., which carries no `code`. `access_denied` is the
      // OAuth value for "the user said no", which is not a fault and should not read like one.
      const k12Error = searchParams.get("error");
      if (k12Error) {
        clearQueryFromHistory();
        failToLogin(
          k12Error === "access_denied"
            ? "k12_login_denied"
            : "k12_login_provider_failed"
        );
        return;
      }

      const authorizationCode = searchParams.get("code");
      if (!authorizationCode) {
        clearQueryFromHistory();
        failToLogin("k12_login_callback_incomplete");
        return;
      }

      // CSRF: the callback must belong to the flow THIS tab started. Without it, an attacker who obtains any
      // valid authorization code can hand a victim a crafted callback URL and have the victim's browser
      // complete a login as the attacker's K12 identity.
      const expectedState = window.sessionStorage.getItem(K12_STATE_STORAGE_KEY);
      window.sessionStorage.removeItem(K12_STATE_STORAGE_KEY);
      if (!expectedState || searchParams.get("state") !== expectedState) {
        clearQueryFromHistory();
        // Overwhelmingly a stale tab or a re-opened link rather than an attack, and the fix ("start again
        // from K12") is the same either way — so the message says that instead of implying wrongdoing.
        failToLogin("k12_login_link_expired");
        return;
      }

      clearQueryFromHistory();

      const { data, err } = await loginWithK12AuthorizationCode({
        code: authorizationCode,
        redirectUri: K12_REDIRECT_URI,
      });
      if (err) {
        // The backend separates the causes that matter: a code that will not exchange (theirs or the
        // network's, and a re-opened callback URL lands here too, since a code is single-use), a role
        // outside the integration, and by far the most common one — a pupil K12 knows but whose roster was
        // never imported (`k12UserIsNotProvisioned`). That last one used to surface as "invalid
        // credentials", which sent schools looking for a password problem that did not exist.
        // Our own API being down (a deploy refuses connections for minutes) is not the provider's fault.
        // Pasif öğrenci: the school has not opened the platform for this learner — the explanation screen.
        if (isLearnerProfileDeactivatedError(err)) {
          forgetDeactivatedProfile();
          setBoarding("PROFILE_INACTIVE");
          return;
        }
        failToLogin(
          isServerUnavailableError(err)
            ? "sso_server_unavailable_text"
            : resolveBackendMessageKey(err, "k12_login_provider_failed")
        );
        return;
      }

      const redirectUrl = (data as any)?.redirectUrl;
      if (!redirectUrl) {
        failToLogin("k12_login_failed");
        return;
      }
      // Not `window.location.href = redirectUrl` — see `completeHandoffOrNavigate`.
      completeHandoffOrNavigate(redirectUrl, failToLogin, "k12_login_failed");
    };

    // Leg 3 first. The hand-off returns to this same route, and its fragment is the only unambiguous
    // marker — testing the query first would misread a hand-off that still carried a stale query string.
    if (carriesHandoffTokens(hash)) {
      completeHandoffLogin(hash, search, failToLogin);
      return;
    }
    // NOT `searchParams.size`. That property is a 2023 addition (Chrome 113 / Safari 17 / Firefox 112),
    // and this app's browserslist still covers older Safari and Chromium — there it is `undefined`, the
    // comparison is false, and the ENTRY leg becomes unreachable: clicking "K12Net ile giriş yap" falls
    // through to the callback leg, finds no `code`, and bounces back to /login blaming the link. jsdom in
    // this repo's Jest version lacks it too, so a unit test would exercise the wrong leg and agree.
    if (!Array.from(searchParams.keys()).length) {
      const phonePlatform = detectPhonePlatform(userAgent);
      if (phonePlatform) {
        offerMobileApp(phonePlatform);
        return;
      }
      startK12Login();
      return;
    }
    completeK12Callback();
  }, []);

  if (mobileAppHandoff) {
    const { kind, platform, appUrl } = mobileAppHandoff;
    return (
      <div className="raj-root">
        <style>{loginWithK12ScreenStyles}</style>
        <MobileAppHandoffCard
          title={i18n.t(
            kind === "entry"
              ? "k12_mobile_app_title"
              : "k12_mobile_app_returning_title"
          )}
          description={i18n.t(
            kind === "entry"
              ? "k12_mobile_app_description"
              : "k12_mobile_app_returning_description"
          )}
          primaryAction={{
            title: i18n.t(
              kind === "entry" ? "mobile_app_open" : "mobile_app_return"
            ),
            onClick: () => {
              window.location.href = appUrl;
            },
          }}
          secondaryAction={
            kind === "entry"
              ? {
                  title: i18n.t(
                    platform === "ios"
                      ? "mobile_app_download_ios"
                      : "mobile_app_download_android"
                  ),
                  onClick: () => {
                    window.location.href = storeUrlFor(platform);
                  },
                }
              : undefined
          }
        />
      </div>
    );
  }

  return (
    <div className="raj-root">
      <style>{loginWithK12ScreenStyles}</style>
      <BaseLoadingWithText
        size="large"
        primaryColor="#55777f"
        secondaryColor="55777f"
      />
    </div>
  );
};

export default LoginWithK12Screen;
